import { createRoot } from "react-dom/client";

import "./index.css";
import { AppProviders } from "./providers/AppProvider.tsx";
import { RotateDeviceOverlay } from "./shared/components/ui/RotateDeviceOverlay/RotateDeviceOverlay";

/**
 * Routes are code-split via React.lazy (see routes/index.tsx). Each lazy
 * chunk's JS/CSS filename is content-hashed by Vite and deleted on every
 * new deploy. A tab left open since before a deploy still has the OLD
 * index-*.js loaded — navigating to a route it hasn't fetched yet tries to
 * preload a chunk by its old hash, which 404s ("Unable to preload CSS for
 * ..."). Vite emits this event specifically for that case; reloading picks
 * up the current index.html and current asset hashes instead of leaving
 * the user stuck on a broken lazy import.
 *
 * One-shot guard: nginx.conf now sends index.html as no-cache, so a reload
 * should always fetch the current build. But if something upstream (a CDN,
 * a proxy) ever re-caches it anyway, reloading unconditionally here would
 * loop forever — same stale index.html, same dead chunk hash, same error.
 * sessionStorage survives the reload, so a second preload error within the
 * same tab is treated as that loop and left unhandled rather than retried
 * forever. The flag is cleared 5s after a load that DIDN'T hit this
 * listener, so a genuinely new incident on a later deploy still gets its
 * one automatic retry.
 */
const PRELOAD_RETRY_KEY = "vitePreloadReloaded";
window.addEventListener("vite:preloadError", () => {
  if (sessionStorage.getItem(PRELOAD_RETRY_KEY)) return;
  sessionStorage.setItem(PRELOAD_RETRY_KEY, "1");
  window.location.reload();
});
setTimeout(() => sessionStorage.removeItem(PRELOAD_RETRY_KEY), 5000);

createRoot(document.getElementById("root")!).render(
  <>
    {/* Mounted once, outside the whole app tree — pure CSS (media query)
        driven, so it covers every page/route/error-state for free. */}
    <RotateDeviceOverlay />
    <AppProviders />
  </>,
);
